import { Controller } from "@hotwired/stimulus"

// data-controller="clipboard" data-clipboard-text-value="..." with a button data-action="clipboard#copy".
// Large text: a `source` target (a hidden textarea, or any element) is copied instead of the value.
// Feedback: a `label` target gets its text swapped; otherwise a small "Copied"
// badge appears next to the button for a moment (icon-only buttons).
export default class extends Controller {
  static values = { text: String, done: { type: String, default: "Copied" }, failed: { type: String, default: "Copy failed" } }
  static targets = ["label", "source"]

  async copy(event) {
    // currentTarget is gone once the event has been dispatched, i.e. after any await.
    const button = event.currentTarget
    const text = this.hasSourceTarget ? (this.sourceTarget.value ?? this.sourceTarget.textContent) : this.textValue
    const ok = await this.write(text)
    if (this.hasLabelTarget) return this.swapLabel(ok)
    this.flash(button, ok)
  }

  // navigator.clipboard exists only in secure contexts (https, localhost); a
  // LAN address or an embedded browser gets the old execCommand path.
  async write(text) {
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true }
    } catch (_) { /* fall through */ }
    const area = document.createElement("textarea")
    area.value = text
    area.setAttribute("readonly", "")
    area.style.position = "fixed"
    area.style.opacity = "0"
    document.body.appendChild(area)
    area.select()
    let ok = false
    try { ok = document.execCommand("copy") } catch (_) { ok = false }
    area.remove()
    return ok
  }

  swapLabel(ok) {
    const original = this.labelTarget.textContent
    this.labelTarget.textContent = ok ? this.doneValue : this.failedValue
    setTimeout(() => { this.labelTarget.textContent = original }, 1500)
  }

  flash(button, ok) {
    const badge = document.createElement("span")
    badge.className = `copied-badge${ok ? "" : " failed"}`
    badge.textContent = ok ? this.doneValue : this.failedValue
    button.insertAdjacentElement("afterend", badge)
    requestAnimationFrame(() => badge.classList.add("show"))
    setTimeout(() => { badge.classList.remove("show"); setTimeout(() => badge.remove(), 200) }, 1400)
  }
}
